/**
 * Site builder AI (contract: POST /api/user/sites/{id}/ai/<kind> → {job_id}; poll GET /{id}/ai/jobs/{jobId} →
 * {status: queued|running|done|failed, result, error}; GET /api/user/sites/ai/credit → {bonus…, quota_usd, topup_url};
 * 402 {error_code: llm_quota_insufficient, topup_url} = out of quota).
 * runAi(siteId, kind, body, {onStatus, signal}) resolves to the job result; rejects with
 *   { soon: true } when the AI endpoints are not deployed (404/405/501),
 *   { quota: true, topup_url } on 402,
 *   { error } otherwise.
 */
import React, { useEffect, useState } from 'react';
import { Button, Progress, Space, Tag, Tooltip, message } from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import { t as tt, useTranslation } from '../../../shared/i18n';
import { QUIET, sb } from './api';
import { formatMoney } from '../../../shared/utils/money';

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const SOON = [404, 405, 501];

const classify = (e) => {
    const b = e?.body || {};
    if (e?.status === 402 || b.error_code === 'llm_quota_insufficient' || b.code === 'llm_quota_insufficient') {
        return { quota: true, topup_url: b.topup_url || b.data?.topup_url, error: b.error || e?.error };
    }
    if (SOON.includes(e?.status)) return { soon: true };
    return { error: e?.error || e?.message || tt('AI request failed'), toasted: e?.toasted };
};

export async function runAi(siteId, kind, body, { onStatus, signal, timeoutMs = 300000 } = {}) {
    let job;
    try {
        job = await sb.post(`/${siteId}/ai/${kind}`, body, { ...QUIET, timeout: 60000 });
    } catch (e) {
        throw classify(e);
    }
    const jobId = job?.job_id || job?.id;
    if (!jobId) {
        if (job && (job.status === 'done' || job.result)) return job.result ?? job;
        throw { error: tt('AI request failed') }; // eslint-disable-line no-throw-literal
    }
    const started = Date.now();
    let delay = 1200;
    while (Date.now() - started < timeoutMs) {
        if (signal?.aborted) throw { aborted: true }; // eslint-disable-line no-throw-literal
        await sleep(delay);
        delay = Math.min(4000, delay + 400);
        let st;
        try {
            st = await sb.get(`/${siteId}/ai/jobs/${jobId}`, null, QUIET);
        } catch (e) {
            const c = classify(e);
            if (c.quota || c.soon) throw c;
            continue; // transient: keep polling
        }
        onStatus?.(st?.status, st);
        if (st?.status === 'done') return st.result ?? st;
        if (st?.status === 'failed') {
            const r = st.result || {};
            if (st.error_code === 'llm_quota_insufficient' || r.error_code === 'llm_quota_insufficient') throw { quota: true, topup_url: st.topup_url || r.topup_url }; // eslint-disable-line no-throw-literal
            throw { error: st.error || r.error || tt('AI request failed') }; // eslint-disable-line no-throw-literal
        }
    }
    throw { error: tt('The AI is taking too long. Try again in a moment') }; // eslint-disable-line no-throw-literal
}

/** standard reporting for runAi failures; returns true when it was "coming soon" */
export const reportAiError = (e) => {
    if (e?.aborted) return false;
    if (e?.soon) { message.info(tt('AI generation is coming soon')); return true; }
    if (e?.quota) {
        const key = 'yc-ai-quota';
        message.warning({
            key,
            duration: 8,
            content: (
                <Space>
                    {tt('Your AI credit is used up.')}
                    {e.topup_url && <Button size="small" type="primary" onClick={() => { message.destroy(key); window.location.href = e.topup_url; }}>{tt('Top up')}</Button>}
                </Space>
            ),
        });
        return false;
    }
    if (!e?.toasted) message.error(e?.error || tt('AI request failed'));
    return false;
};

let creditCache = null;
let creditAt = 0;
export const loadCredit = async (force = false) => {
    if (!force && creditCache && Date.now() - creditAt < 60000) return creditCache;
    try {
        creditCache = await sb.get('/ai/credit', null, QUIET);
    } catch (_) {
        creditCache = { unavailable: true };
    }
    creditAt = Date.now();
    return creditCache;
};

/** small badge with the remaining AI credit (hidden while the endpoint is missing) */
export const AiCreditBadge = ({ refreshKey }) => {
    const { t } = useTranslation();
    const [c, setC] = useState(creditCache);
    useEffect(() => { let alive = true; loadCredit(!!refreshKey).then((d) => alive && setC(d)); return () => { alive = false; }; }, [refreshKey]);
    if (!c || c.unavailable) return null;
    const usd = c.quota_usd ?? c.balance_usd ?? c.quota ?? c.remaining_usd;
    const bonus = c.bonus && typeof c.bonus === 'object' ? c.bonus : null;
    const label = usd !== undefined && usd !== null ? formatMoney(usd, 'USD', { original: true }) : null;
    if (!label && !bonus) return null;
    const low = usd !== undefined && Number(usd) <= 0;
    return (
        <Tooltip title={bonus?.label || bonus?.status ? `${t('AI credit')} · ${bonus.label || t(bonus.status)}` : t('AI credit')}>
            <Tag color={low ? 'red' : 'purple'} icon={<ThunderboltOutlined />} style={{ marginInlineEnd: 0, cursor: c.topup_url ? 'pointer' : undefined }}
                onClick={() => { if (low && c.topup_url) window.location.href = c.topup_url; }}>
                {label || t('AI')}
            </Tag>
        </Tooltip>
    );
};

/** start a job without waiting (long jobs such as a whole site); resolves to the job id */
export async function startAi(siteId, kind, body) {
    try {
        const job = await sb.post(`/${siteId}/ai/${kind}`, body, { ...QUIET, timeout: 60000 });
        return job?.job_id || job?.id;
    } catch (e) {
        throw classify(e);
    }
}

const ACTIVE = ['queued', 'running'];
const KIND_LABEL = { site: 'Building your site with AI…', page: 'Generating the page with AI…', translate: 'Translating…', translate_catalog: 'Translating…', component: 'Creating a component with AI…' };
/** result.progress {done, total, step} of a running job → short line, e.g. "Generating pages 1/3" */
export const progressText = (pr) => {
    if (!pr || !pr.step) return '';
    const n = { done: Number(pr.done) || 0, total: Number(pr.total) || 0 };
    switch (pr.step) {
        case 'plan': return tt('Planning the site');
        case 'pages': return n.total ? tt('Generating pages {{done}}/{{total}}', n) : tt('Generating pages');
        case 'sections': return n.total ? tt('Generating sections {{done}}/{{total}}', n) : tt('Generating sections');
        case 'texts': return n.total ? tt('Translating {{done}}/{{total}}', n) : tt('Translating…');
        case 'repair': return tt('Checking the pages');
        case 'saving': return tt('Saving');
        case 'done': return tt('Finishing');
        default: return '';
    }
};

/**
 * Banner for long AI jobs of a site (polls GET /{id}/ai/jobs while one is queued/running). The job runs on the
 * server, so the user may leave the page; onDone(job) fires when a job that was running here finishes.
 */
export const AiJobBanner = ({ siteId, onDone, kinds = ['site', 'page', 'translate'] }) => {
    const { t } = useTranslation();
    const [active, setActive] = useState([]);
    const [progress, setProgress] = useState(null);
    const seen = React.useRef(new Map());
    const tick = React.useRef(0);
    useEffect(() => {
        let alive = true;
        let timer = null;
        const poll = async () => {
            let items = [];
            try { items = (await sb.get(`/${siteId}/ai/jobs`, { limit: 10 }, QUIET))?.items || []; } catch (_) { return; }
            if (!alive) return;
            const relevant = items.filter((j) => kinds.includes(j.kind));
            relevant.forEach((j) => {
                const before = seen.current.get(j.id);
                if (before && ACTIVE.includes(before) && !ACTIVE.includes(j.status)) {
                    if (j.status === 'done') { message.success(t('AI finished')); onDone?.(j); }
                    else if (j.status === 'failed') reportAiError(j.error_code === 'llm_quota_insufficient' ? { quota: true, topup_url: j.topup_url || j.result?.topup_url } : { error: j.error || tt('AI request failed') });
                }
                seen.current.set(j.id, j.status);
            });
            const act = relevant.filter((j) => ACTIVE.includes(j.status));
            setActive(act);
            if (act[0]?.status === 'running') {
                try {
                    const one = await sb.get(`/${siteId}/ai/jobs/${act[0].id}`, null, QUIET);
                    if (alive) setProgress(one?.result?.progress ? { id: act[0].id, ...one.result.progress } : null);
                } catch (_) { /* progress is cosmetic */ }
            } else setProgress(null);
            tick.current += 1;
            if (act.length) timer = setTimeout(poll, 5000);
        };
        poll();
        const kick = () => { if (!timer || !active.length) poll(); };
        window.addEventListener('yc-ai-job-started', kick);
        return () => { alive = false; clearTimeout(timer); window.removeEventListener('yc-ai-job-started', kick); };
    }, [siteId]); // eslint-disable-line react-hooks/exhaustive-deps
    if (!active.length) return null;
    const j = active[0];
    const started = j.started_at || j.created_at;
    const mins = started ? Math.max(0, Math.round((Date.now() - new Date(String(started).replace(' ', 'T') + 'Z').getTime()) / 60000)) : 0;
    const pr = progress && progress.id === j.id ? progress : null;
    const prLine = j.status === 'running' ? progressText(pr) : '';
    const pct = j.status === 'queued' ? 5
        : pr?.step === 'done' ? 98
            : pr?.total ? Math.min(95, 10 + Math.round((80 * (Number(pr.done) || 0)) / Number(pr.total)) + (pr.step === 'repair' ? 5 : pr.step === 'saving' ? 8 : 0))
                : Math.min(95, 10 + mins * 20);
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: 'var(--ant-color-info-bg, #e6f4ff)', fontSize: 13 }}>
            <ThunderboltOutlined spin={false} style={{ color: '#2357ff' }} />
            <span style={{ flex: 1 }}>{t(KIND_LABEL[j.kind] || 'AI is working…')} {j.status === 'queued' ? t('Queued') : (prLine ? <b style={{ fontWeight: 500 }}>{prLine}</b> : t('{{n}} min', { n: mins }))} · <span style={{ opacity: 0.7 }}>{t('You can leave this page.')}</span></span>
            <Progress percent={pct} size="small" showInfo={false} status="active" style={{ width: 120, margin: 0 }} />
        </div>
    );
};
